import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import { OUTLINE_CELL_ROW_GUTTER_WIDTH, OUTLINE_MIN_COLUMN_WIDTH } from "../../constants";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { TABLE_VIEW_HEADER } from "../../constants/identifiers";
import { fixedAndStickyColumnIds } from "../../constants/outlineColumnIds";
import { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { immutableRecords, PropTypeShapes } from "../../types";
import { withContainerContext } from "../container/ContainerContext";
import { withXScrollPositionContext } from "../scrollable/ScrollPositionContext";
import TableHeaderCellView from "./TableHeaderCellView";
import TableRowGutterView from "./TableRowGutterView";

const canDropOnComponent = (props, dragItem) => {
  return dragItem.sourceView === props.viewId && dragItem.itemType === DragDropTypes.TABLE_COLUMN;
};

const dropCallbacks = {
  onEnter: (_props, _dragItem, _dropTarget) => {
    // Do nothing
  },

  onLeave: (_props, _dragItem, _dropTarget) => {
    // if (props.onInternalDragLeave) {
    //   props.onInternalDragLeave();
    // }
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return [dragItem.itemId];
  },

  getDragStartParams(_props, _dragItem, _positions, _dropTarget) {
    return {};
  },

  onHover: (props, dragItem, positions, dropTarget) => {
    const canDrop = canDropOnComponent(props, dragItem);
    return canDrop ? dropTarget.component.onHover(props, dragItem, positions, dropTarget) : null;
  },

  onDrop: (props, dragItem, positions, dropTarget) => {
    const canDrop = canDropOnComponent(props, dragItem);
    return canDrop ? dropTarget.component.onDrop(props, dragItem, positions, dropTarget) : null;
  },

  getDropPreviewForItem: (props, dragItem, _dropTarget) => {
    if (dragItem.sourceView != props.viewId || dragItem.itemType != DragDropTypes.TABLE_COLUMN) {
      return <div />;
    } else {
      return null;
    }
  }
};

class TableHeaderView extends PureComponent {
  constructor(props, context) {
    super(props, context);

    this._domNode = null;

    this._liveScrollListeners = {
      onLiveScrollStart: this.onLiveScrollStart.bind(this),
      onLiveScrollUpdate: this.onLiveScrollUpdate.bind(this),
      onLiveScrollStop: this.onLiveScrollStop.bind(this)
    };

    this._stickyDomNode = null;
    this._liveLeft = 0;
    this._horizontalScrollerActive = false;

    this.getStyle = this.getStyle.bind(this);
    this.applyStyle = this.applyStyle.bind(this);
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
    this.props.xScrollActions.registerForLiveUpdates(this._liveScrollListeners);
  }

  componentWillUnmount() {
    this.props.xScrollActions.unregisterForLiveUpdates(this._liveScrollListeners);
  }

  onLiveScrollStart(horizontalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = true;
      this.applyStyle();
    }
  }

  onLiveScrollUpdate(scrollLeft) {
    if (!isNil(scrollLeft) && scrollLeft !== this._liveLeft) {
      this._liveLeft = scrollLeft;
    }
  }

  onLiveScrollStop(horizontalScroller) {
    if (horizontalScroller) {
      this._horizontalScrollerActive = false;
      this.applyStyle();
    }
  }

  applyStyle() {
    const style = this.getStyle();
    this._stickyDomNode.style.left = style.left;
    this._stickyDomNode.style.top = style.top;
    this._stickyDomNode.style.overflow = style.overflow;
    this._stickyDomNode.style.position = style.position;
  }

  getStyle() {
    let left = `${this._liveLeft}px`;
    let top = "0px";
    let overflow = null;
    let position = "absolute";
    const maxWidth = `${this.props.containerRect.width}px`;
    if (this._horizontalScrollerActive) {
      left = `${this.props.containerRect.left}px`;
      top = `${this.props.containerRect.top}px`;
      position = "fixed";
      overflow = "hidden";
    }

    return {
      left,
      top,
      overflow,
      position,
      maxWidth
    };
  }

  onHover(_props, dragItem, positions, _dropTarget) {
    // Find where the column should go...
    const domRect = this._domNode.getBoundingClientRect();
    const visibleColumns = this.props.columns;
    const otherColumns = visibleColumns.filter(c => c.id !== dragItem.itemId);
    const firstLeft = visibleColumns.get(0).left;
    const dragCenter = (positions.sourceRect.left + positions.sourceRect.right) / 2 - domRect.left;

    // Work out where our item currently is in the official list
    const itemIndex = this.props.columns.findIndex(c => c.id === dragItem.itemId);
    const origItem = this.props.columns.get(itemIndex);
    const origItemCenter = origItem.left + origItem.width / 2 - firstLeft;

    let insertIndex = null;
    // Are we moving left or right?
    if (dragCenter < origItemCenter) {
      // Moving left, so as soon as the center crosses a right, we are interested in it...
      const columnRights = otherColumns.map(c => c.left + c.width - firstLeft);
      const firstIndexAfter = columnRights.findIndex(r => r > dragCenter);
      insertIndex = firstIndexAfter === -1 ? columnRights.size : firstIndexAfter;
    } else {
      // Moving right, so as soon as the center crosses a left, switch positions
      const columnLefts = visibleColumns.map(c => c.left - firstLeft);
      const firstIndexAfter = columnLefts.findLastIndex(l => l < dragCenter);
      insertIndex = firstIndexAfter === -1 ? 0 : firstIndexAfter;
    }

    this._dropIndex = Math.max(insertIndex, this.props.lockedColumnCount);

    // If it is the same as it was, no need to send the live update
    if (this._dropIndex !== itemIndex) {
      this.props.onLiveColumnDrag ? this.props.onLiveColumnDrag(dragItem.itemId, this._dropIndex) : null;
    }
  }

  onDrop(_props, dragItem, _positions, _dropTarget) {
    if (isNil(this._dropIndex)) {
      return;
    }
    // Now move the dragged item to the correct index...
    this.props.onColumnDrop ? this.props.onColumnDrop(dragItem.itemId, this._dropIndex) : null;
    this._dropIndex = null;
  }

  renderColumns(columns) {
    return columns.map(c => {
      const onResize = width => {
        if (this.props.onResizeColumn) {
          this.props.onResizeColumn(c.id, Math.max(width, OUTLINE_MIN_COLUMN_WIDTH));
        }
      };
      return (
        <TableHeaderCellView
          tableId={this.props.tableId}
          viewId={this.props.viewId}
          key={c.id}
          id={c.id}
          label={c.label}
          width={c.width}
          canResize={this.props.allowColumnResize}
          onResize={onResize}
          canDrag={
            this.props.allowColumnReorder &&
            c.index >= this.props.lockedColumnCount &&
            !fixedAndStickyColumnIds.includes(c.id)
          }
          isSorted={c.id === this.props.sortByColumnId}
          sortInDesc={this.props.sortInDesc}
          onClick={
            this.props.allowSort && this.props.sortableColumnIds.includes(c.id) ? this.props.onSortByColumn : null
          }
        />
      );
    });
  }

  render() {
    const lastStickyColumn =
      this.props.lockedColumnCount > 0 ? this.props.columns.get(this.props.lockedColumnCount - 1) : null;
    const lastStickyRight = lastStickyColumn ? lastStickyColumn.left + lastStickyColumn.width : 0;

    const lastColumn = this.props.columns.last();
    const lastRight = lastColumn.left + lastColumn.width;

    const stickyWidth =
      lastStickyRight +
      (this.props.showGutter ? scalePixelsToFontSize(OUTLINE_CELL_ROW_GUTTER_WIDTH, this.props.fontSize) : 0);
    const scrollingWidth = lastRight - lastStickyRight;
    const stickyStyle = this.getStyle();
    stickyStyle.width = `${stickyWidth}px`;

    const scrollingStyle = {
      width: `${scrollingWidth}px`,
      left: `${stickyWidth}px`
    };

    return (
      <div className="tableHeader" ref={node => (this._domNode = node)} id={this.props.id}>
        <div className="tableHeaderRow">
          <div className="stickyCells" style={stickyStyle} ref={node => (this._stickyDomNode = node)}>
            {this.props.showGutter ? (
              <TableRowGutterView
                id={`${this.props.tableId}-toggleColumn`}
                icon="rightArrow"
                iconColor="iconMono"
                onClick={this.props.onToggleColumnPopover}
                tooltip="Select columns"
              />
            ) : null}
            {this.renderColumns(this.props.columns.slice(0, this.props.lockedColumnCount))}
          </div>
          <div className="scrollingCells" style={scrollingStyle}>
            {this.renderColumns(this.props.columns.slice(this.props.lockedColumnCount))}
          </div>
        </div>
      </div>
    );
  }
}

TableHeaderView.propTypes = {
  viewId: PropTypes.string.isRequired,
  tableId: PropTypes.string.isRequired,
  id: PropTypes.string,
  height: PropTypes.number.isRequired,
  columns: PropTypeShapes.tableColumnList.isRequired,

  // How many columns on the left are locked in place?
  lockedColumnCount: PropTypes.number.isRequired,

  // Can the user sort the table?
  allowSort: PropTypes.bool,
  sortableColumnIds: ImmutablePropTypes.listOf(PropTypes.string),
  sortByColumnId: PropTypes.string,
  sortInDesc: PropTypes.bool,
  onSortByColumn: PropTypes.func,

  // Show Gutter
  showGutter: PropTypes.bool,
  // Colump selector
  allowColumnSelect: PropTypes.bool,
  onToggleColumnPopover: PropTypes.func,
  // Resize columps
  allowColumnResize: PropTypes.bool,
  onResizeColumn: PropTypes.func,
  // Drag and drop columbs
  allowColumnReorder: PropTypes.bool,
  onLiveColumnDrag: PropTypes.func,
  onColumnDrop: PropTypes.func,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,

  containerRect: PropTypeShapes.flatRect.isRequired,

  fontSize: PropTypes.number.isRequired
};

export default withContainerContext(withXScrollPositionContext(asDropTarget(TableHeaderView, TABLE_VIEW_HEADER)));
